import { Injectable } from '@angular/core';
import {
  ParkingPricingDiscountDetailResponse,
  ParkingStampList,
  ParkingTransactionDetailResponse,
} from '../../models/parking.model';
import {
  AppliedDiscountViewItem,
  DiscountAvailableStamp,
  DiscountLicenseSearchItem,
  DiscountTransactionDetail,
} from '../../models/parking/discount-apply.model';

@Injectable({ providedIn: 'root' })
export class MapperDiscountApplyService {
  private readonly captureBase = 'https://pss-upload.cloud/Parkingcaptures/';

  mapSearchList(res: unknown): DiscountLicenseSearchItem[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((r) => this.mapSearchItem(r));
  }

  mapSearchItem(dto: unknown): DiscountLicenseSearchItem {
    const r = dto as Record<string, unknown>;
    return {
      cardCode: String(r['cardCode'] ?? r['CardCode'] ?? ''),
      entryLicense: String(r['entryLicense'] ?? r['EntryLicense'] ?? ''),
      entryProvince: (r['entryProvince'] ?? r['EntryProvince'] ?? null) as string | null,
      entryDate: (r['entryDate'] ?? r['EntryDate'] ?? null) as string | null,
      entryPhoto: (r['entryPhoto'] ?? r['EntryPhoto'] ?? null) as string | null,
      entryPath: (r['entryPath'] ?? r['EntryPath'] ?? null) as string | null,
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      vehicleTypeName: (r['vehicleTypeName'] ?? r['VehicleTypeName'] ?? null) as string | null,
      entranceId: (r['entranceId'] ?? r['EntranceId'] ?? null) as string | null,
    };
  }

  mapDetail(res: unknown): DiscountTransactionDetail {
    const raw = res as Record<string, unknown>;
    const detailRaw = (raw['detail'] ?? raw['Detail'] ?? {}) as Record<string, unknown>;
    const stampsRaw = (raw['availableStamps'] ?? raw['AvailableStamps'] ?? []) as unknown[];

    return {
      detail: this.mapTransactionDetail(detailRaw),
      availableStamps: stampsRaw.map((s) => this.mapAvailableStamp(s)),
    };
  }

  mapAvailableStamp(dto: unknown): DiscountAvailableStamp {
    const r = dto as Record<string, unknown>;
    return {
      parkingRateId: Number(r['parkingRateId'] ?? r['ParkingRateId'] ?? 0),
      rateId: (r['rateId'] ?? r['RateId'] ?? null) as string | null,
      rateName: (r['rateName'] ?? r['RateName'] ?? null) as string | null,
      groupId: Number(r['groupId'] ?? r['GroupId'] ?? 0),
      groupName: (r['groupName'] ?? r['GroupName'] ?? null) as string | null,
      costType: String(r['costType'] ?? r['CostType'] ?? 'free'),
      cpCustomerId: (r['cpCustomerId'] ?? r['CpCustomerId'] ?? null) as number | null,
      alreadyStamped: !!(r['alreadyStamped'] ?? r['AlreadyStamped'] ?? false),
      hasQuotaRemaining: !!(r['hasQuotaRemaining'] ?? r['HasQuotaRemaining'] ?? false),
      remainingQty: (r['remainingQty'] ?? r['RemainingQty'] ?? null) as number | null,
      limitType: (r['limitType'] ?? r['LimitType'] ?? null) as string | null,
    };
  }

  mapAvailableStampList(res: unknown): DiscountAvailableStamp[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as unknown[]).map((s) => this.mapAvailableStamp(s));
  }

  resolveCaptureUrl(item: { entryPath?: string | null; entryPhoto?: string | null }): string | null {
    const path = (item.entryPath || item.entryPhoto || '').trim();
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    return this.captureBase + path.replace(/^\//, '');
  }

  buildAppliedDiscounts(detail: ParkingTransactionDetailResponse | null): AppliedDiscountViewItem[] {
    if (!detail) return [];
    const items: AppliedDiscountViewItem[] = [];

    const taiwan = detail.parkingPricing?.discountDetails ?? [];
    for (const d of taiwan) {
      items.push({
        source: 'taiwan',
        title: d.ruleName || 'ส่วนลด Taiwan',
        subTitle: d.discountTime > 0 ? `${d.discountTime} นาที` : 'Taiwan pricing',
        amountLabel: `-${Number(d.discountFee || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
        at: null,
      });
    }

    const stamps = detail.parkingStampLists ?? [];
    for (const s of stamps) {
      items.push({
        source: 'stamp',
        title: s.stampName || `Rate #${s.rateId}`,
        subTitle: s.userStamp ? `โดย ${s.userStamp}` : 'parking_web_stamp',
        amountLabel: s.remark || '—',
        at: s.stampDate ? String(s.stampDate) : null,
      });
    }

    return items;
  }

  formatDuration(entryDate: string | null | undefined): string {
    if (!entryDate) return '—';
    const start = new Date(entryDate);
    if (Number.isNaN(start.getTime())) return '—';
    const mins = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h} ชม. ${m} นาที`;
  }

  private mapTransactionDetail(r: Record<string, unknown>): ParkingTransactionDetailResponse {
    const pricing = (r['parkingPricing'] ?? r['ParkingPricing'] ?? {}) as Record<string, unknown>;
    const discountDetails = (pricing['discountDetails'] ?? pricing['DiscountDetails'] ?? []) as Record<string, unknown>[];
    const stamps = (r['parkingStampLists'] ?? r['ParkingStampLists'] ?? []) as Record<string, unknown>[];
    const finances = (r['parkingFinanceLists'] ?? r['ParkingFinanceLists'] ?? []) as Record<string, unknown>[];
    const coupons = (r['cityParkingCoupons'] ?? r['CityParkingCoupons'] ?? []) as Record<string, unknown>[];

    return {
      cardCode: String(r['cardCode'] ?? r['CardCode'] ?? ''),
      cardName: String(r['cardName'] ?? r['CardName'] ?? ''),
      entryDate: String(r['entryDate'] ?? r['EntryDate'] ?? ''),
      exitDate: String(r['exitDate'] ?? r['ExitDate'] ?? ''),
      entryLicense: String(r['entryLicense'] ?? r['EntryLicense'] ?? ''),
      entryGateId: Number(r['entryGateId'] ?? r['EntryGateId'] ?? 0),
      exitGateId: Number(r['exitGateId'] ?? r['ExitGateId'] ?? 0),
      vehicleTypeId: Number(r['vehicleTypeId'] ?? r['VehicleTypeId'] ?? 0),
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      remark: String(r['remark'] ?? r['Remark'] ?? ''),
      cardTypeId: Number(r['cardTypeId'] ?? r['CardTypeId'] ?? 0),
      member: !!(r['member'] ?? r['Member'] ?? false),
      encrypt: String(r['encrypt'] ?? r['Encrypt'] ?? ''),
      parkingPricing: {
        parkingOvernight: Number(pricing['parkingOvernight'] ?? pricing['ParkingOvernight'] ?? 0),
        parkingFee: Number(pricing['parkingFee'] ?? pricing['ParkingFee'] ?? 0),
        penaltyFee: Number(pricing['penaltyFee'] ?? pricing['PenaltyFee'] ?? 0),
        paidParking: Number(pricing['paidParking'] ?? pricing['PaidParking'] ?? 0),
        paidPenalty: Number(pricing['paidPenalty'] ?? pricing['PaidPenalty'] ?? 0),
        discount: Number(pricing['discount'] ?? pricing['Discount'] ?? 0),
        totalPay: Number(pricing['totalPay'] ?? pricing['TotalPay'] ?? 0),
        discountDetails: discountDetails.map((d) => ({
          ruleName: String(d['ruleName'] ?? d['RuleName'] ?? ''),
          discountTime: Number(d['discountTime'] ?? d['DiscountTime'] ?? 0),
          discountFee: Number(d['discountFee'] ?? d['DiscountFee'] ?? 0),
        } as ParkingPricingDiscountDetailResponse)),
      },
      parkingStampLists: stamps.map((s) => ({
        rateId: Number(s['rateId'] ?? s['RateId'] ?? 0),
        stampName: String(s['stampName'] ?? s['StampName'] ?? ''),
        stampDate: (s['stampDate'] ?? s['StampDate'] ?? '') as string,
        userStamp: String(s['userStamp'] ?? s['UserStamp'] ?? ''),
        remark: String(s['remark'] ?? s['Remark'] ?? ''),
      } as ParkingStampList)),
      parkingFinanceLists: finances.map((f) => ({
        receiptNo: String(f['receiptNo'] ?? f['ReceiptNo'] ?? ''),
        feeName: String(f['feeName'] ?? f['FeeName'] ?? ''),
        amount: Number(f['amount'] ?? f['Amount'] ?? 0),
        feeTypeName: String(f['feeTypeName'] ?? f['FeeTypeName'] ?? ''),
        createDate: (f['createDate'] ?? f['CreateDate'] ?? '') as string,
      })),
      cityParkingCoupons: coupons.map((c) => ({
        id: Number(c['id'] ?? c['Id'] ?? 0),
        name: (c['name'] ?? c['Name'] ?? null) as string | null,
        code: (c['code'] ?? c['Code'] ?? null) as string | null,
        details: (c['details'] ?? c['Details'] ?? null) as string | null,
        enable: (c['enable'] ?? c['Enable'] ?? null) as boolean | null,
        useDate: (c['useDate'] ?? c['UseDate'] ?? null) as string | null,
        siteId: (c['siteId'] ?? c['SiteId'] ?? null) as number | null,
        createDate: (c['createDate'] ?? c['CreateDate'] ?? null) as string | null,
      })),
    };
  }
}
